import React, { useCallback } from 'react';
import { 
  MDBModal, 
  MDBModalDialog, 
  MDBModalContent, 
  MDBModalHeader, 
  MDBModalTitle,
  MDBModalBody,
  MDBBtn,
  MDBRow,
  MDBCol
} from 'mdb-react-ui-kit';
import { Box } from "@mui/material";
import { useTranslation } from 'react-i18next';

import { StepperNavigation } from '../stepper/index.ts';
import AdminOnboardingStepRenderer from './AdminOnboardingStepRenderer';
import { useAdminOnboardingSteps } from './hooks/useAdminOnboardingSteps';
import { AdminFormProvider } from './components/AdminFormProvider';

export default function AdminOnboardingModal({ 
  isOpen, 
  onClose, 
  companyId, 
  onComplete 
}) {
  const { t } = useTranslation();
  const {
    steps,
    setSteps,
    activeStepId,
    completedSteps,
    isAllComplete,
    markStepComplete,
    navigateToStep,
    canNavigateToStep,
    resetSteps
  } = useAdminOnboardingSteps();

  // Handle step completion
  const handleStepComplete = useCallback((stepId) => {
    markStepComplete(stepId);
    
    // If all steps are completed, call the completion callback
    const newCompletedSteps = new Set([...completedSteps, stepId]);
    if (steps.every(step => newCompletedSteps.has(step.id))) {
      setTimeout(() => {
        onComplete?.();
      }, 500);
    }
  }, [markStepComplete, completedSteps, steps, onComplete]);

  // Handle step navigation from stepper
  const handleStepChange = useCallback((step) => {
    if (step.id) {
      navigateToStep(step.id);
    }
  }, [navigateToStep]);

  // Handle modal close
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  // Handle modal close without confirmation
  const handleCloseWithoutConfirmation = useCallback(() => {
    handleClose();
  }, [handleClose]);

  return (
    <AdminFormProvider>
      <MDBModal 
        staticBackdrop 
        open={isOpen} 
        onClose={handleCloseWithoutConfirmation}
        tabIndex='-1'
      >
        <MDBModalDialog size="fullscreen" centered className="p-xl-5">
          <MDBModalContent style={{ backgroundColor: "#fafafa", height: "100%" }}>
          {/* Header similar to lecture form */}
          <div className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom bg-white">
            <div>
              <h5 className="mb-0">{t('Administrator Onboarding - Welcome!')}</h5>
              <small className="text-muted">{t('Complete the following steps for setup')}</small>
            </div>
            <div className="d-flex align-items-center">
              {/* Progress indicator in header */}
              <span className="me-3 small text-muted">
                {completedSteps.size}/{steps.length} {t('completed')}
              </span>
            </div>
          </div>
          
          {/* Main content area - exact copy from lecture form */}
          <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
            {/* Navigation - fixed width, no scroll, vertically centered */}
            <div className="pe-5 border-end d-flex justify-content-center align-items-center" style={{ flexShrink: 0 }}>
              <div>
                <StepperNavigation
                  steps={steps}
                  setSteps={setSteps}
                  onStepChange={handleStepChange}
                  activeStep={activeStepId}
                  multiExpand={false}
                  size="xl"
                  canNavigateToStep={canNavigateToStep}
                  highlightIncomplete={false}
                />
              </div>
            </div>
            
            {/* Content area - scrollable, exact copy from lecture form */}
            <div className="flex-grow-1 px-4 py-4" style={{ overflow: "hidden" }}>
              <div className="h-100" style={{ overflowY: "auto", overflowX: "hidden" }}>
                <AdminOnboardingStepRenderer
                  activeStepId={activeStepId}
                  onStepComplete={handleStepComplete}
                  onStepChange={handleStepChange}
                  companyId={companyId}
                  isStepCompleted={(stepId) => completedSteps.has(stepId)}
                />
              </div>
            </div>
          </div>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </AdminFormProvider>
  );
}